import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import ModalComponent from '../components/modal/modal.jsx';
import ExtensionPreviewComponent from '../components/extension-preview/extension-preview.jsx';
import styles from '../components/loaded-extensions/loaded-extensions.css';
import addExtensionIcon from '../components/gui/icon--extensions.svg';

const DEFAULT_COLOR = '#4c97ff';

// TurboWarp-style extensions declare Description/Version as leading comments
// (`// Description: ...`, optionally inside a /* */ block); getInfo() does not
// carry them, so they can only be recovered by reading the source.
const parseExtensionHeader = source => {
    const result = {};
    if (typeof source !== 'string' || !source) {
        return result;
    }
    let header = '';
    const blockMatch = source.match(/^\s*\/\*([\s\S]*?)\*\//);
    if (blockMatch) {
        header = blockMatch[1];
    } else {
        const lineMatches = source.match(/^\s*(?:\/\/[^\n]*\n?)+/);
        if (lineMatches) {
            header = lineMatches[0];
        }
    }
    for (let rawLine of header.split('\n')) {
        const line = rawLine.replace(/^\s*\/\/?\/?/, '').replace(/^\s*\*\s?/, '');
        if (!line.trim()) {
            continue;
        }
        let key;
        let value;
        const colonIdx = line.indexOf(':');
        if (colonIdx !== -1) {
            key = line.slice(0, colonIdx).trim().toLowerCase();
            value = line.slice(colonIdx + 1).trim();
        } else {
            const spaceIdx = line.search(/\s+/);
            if (spaceIdx === -1) {
                key = line.trim().toLowerCase();
                value = '';
            } else {
                key = line.slice(0, spaceIdx).trim().toLowerCase();
                value = line.slice(spaceIdx + 1).trim();
            }
        }
        if (key === 'description' && value && !result.description) {
            result.description = value;
        } else if (key === 'version' && value && !result.version) {
            result.version = value;
        }
    }
    return result;
};

// Cache the parsed header per extension URL. The cached value may be a pending
// promise, so concurrent lookups share a single fetch.
const headerCache = new Map();
const fetchHeader = url => {
    if (!url) {
        return Promise.resolve({});
    }
    if (headerCache.has(url)) {
        return headerCache.get(url);
    }
    const promise = fetch(url)
        .then(res => (res.ok ? res.text() : ''))
        .then(text => parseExtensionHeader(text))
        .catch(() => ({}));
    headerCache.set(url, promise);
    return promise;
};

// Scratch/TurboWarp convention: extension icon URIs use the `@image:` prefix
// to mean "path relative to the extension's source URL" (or, on desktop builds,
// sometimes an absolute filesystem path). Blockly resolves this internally for
// the category menu, but our modal uses <img src> directly, so we resolve it
// here. data:/http(s)/file URLs and bare data URIs pass through unchanged.
const toFileURL = path => {
    if (!path) return null;
    // Windows: "C:\foo\bar" or "C:/foo/bar"
    const winMatch = path.match(/^([a-zA-Z]):[\\/](.*)$/);
    if (winMatch) {
        return 'file:///' + winMatch[1] + ':/' + winMatch[2].replace(/\\/g, '/');
    }
    // POSIX: /foo/bar
    if (path.startsWith('/')) {
        return 'file://' + path;
    }
    return null;
};
const resolveIconURL = (iconURI, extensionURL) => {
    if (!iconURI || typeof iconURI !== 'string') return null;
    const schemeEnd = iconURI.indexOf(':');
    const scheme = schemeEnd > 0 ? iconURI.slice(0, schemeEnd).toLowerCase() : '';
    if (scheme === 'data' || scheme === 'http' || scheme === 'https' || scheme === 'file') {
        return iconURI;
    }
    if (iconURI.startsWith('@image:')) {
        const rel = iconURI.slice('@image:'.length);
        if (!rel) return null;
        // Absolute filesystem path → convert to file:// URL so <img> can load it.
        const fileURL = toFileURL(rel);
        if (fileURL) return fileURL;
        // Otherwise resolve relative to the extension's source URL.
        if (extensionURL) {
            try {
                return new URL(rel, extensionURL).href;
            } catch (e) {
                // fall through
            }
        }
        return null;
    }
    return iconURI;
};

// Fetch a file:// icon and inline it as a data: URL. Electron's webSecurity
// blocks <img src="file://..."> from a non-file origin, so when the icon came
// from an absolute filesystem path the direct file:// URL fails to load.
// Reading the blob via fetch() and inlining it sidesteps that restriction.
const fetchIconAsDataURL = src => {
    if (!src || !src.startsWith('file://')) {
        return Promise.resolve(null);
    }
    return fetch(src)
        .then(res => (res.ok ? res.blob() : null))
        .then(blob => {
            if (!blob) return null;
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        })
        .catch(() => null);
};

// Snapshot every extension currently loaded into the VM. openwarp-vm's
// ExtensionManager exposes getLoadedExtensions() -> {id, info}, where info is
// the extension's own getInfo() (sanitized and cached by the VM at registration
// time), so this is synchronous and safe for both built-in and worker extensions.
const collectLoadedExtensions = vm => {
    const em = vm && vm.extensionManager;
    if (!em || typeof em.getLoadedExtensions !== 'function') {
        return [];
    }
    const urls = (typeof em.getExtensionURLs === 'function') ? em.getExtensionURLs() : {};
    return em.getLoadedExtensions().map(({id, info}) => {
        const rawIcon = info && (info.blockIconURI || info.menuIconURI || info.iconURL);
        return {
            id,
            name: (info && info.name) || id,
            description: (info && info.description) || '',
            version: (info && info.version) ? String(info.version) : '',
            iconURL: resolveIconURL(rawIcon, urls[id] || null),
            color: (info && info.color1) || DEFAULT_COLOR,
            url: urls[id] || null
        };
    });
};

// How many blocks from this extension are currently placed in any target.
const countUsages = (vm, extensionId) => {
    const prefix = `${extensionId}_`;
    let count = 0;
    try {
        for (const target of vm.runtime.targets) {
            const blocks = target.blocks && target.blocks._blocks;
            if (!blocks) {
                continue;
            }
            for (const blockId of Object.keys(blocks)) {
                const block = blocks[blockId];
                if (block && typeof block.opcode === 'string' && block.opcode.startsWith(prefix)) {
                    count++;
                }
            }
        }
    } catch (e) {
        return 0;
    }
    return count;
};

// Remove just the extension's own blocks from every target, leaving the rest of
// any containing script intact. The engine's deleteBlock() would otherwise tear
// out everything below a block, so before deleting each block we first "unplug"
// it from its parent and hand its tail (block.next) back up: if it sat mid-stack
// we splice the parent's next onto the tail, if it was a stack top we promote the
// tail to a new stack root. Only then do we null its next and let deleteBlock()
// remove the block itself (plus its own private input/shadow subtrees).
const removeExtensionBlocks = (vm, extensionId) => {
    const runtime = vm && vm.runtime;
    const prefix = `${extensionId}_`;
    if (!runtime || !Array.isArray(runtime.targets)) return;

    const removeSingleBlock = (blocks, blockId) => {
        const block = blocks._blocks[blockId];
        if (!block) return;
        const nextId = block.next;
        const parentId = block.parent;

        if (parentId && blocks._blocks[parentId]) {
            const parent = blocks._blocks[parentId];
            if (parent.next === blockId) {
                // Mid-stack: splice the parent straight onto this block's tail.
                parent.next = nextId;
                if (nextId && blocks._blocks[nextId]) {
                    blocks._blocks[nextId].parent = parentId;
                }
            } else {
                // Nested as an input (reporter/boolean): detach it from the input slot.
                for (const inputName in parent.inputs) {
                    const input = parent.inputs[inputName];
                    if (input && input.block === blockId) {
                        input.block = null;
                        break;
                    }
                }
            }
        } else if (nextId && blocks._blocks[nextId]) {
            // Was a stack top: promote its tail to be the new stack root.
            blocks._blocks[nextId].parent = null;
        }

        // Isolate this block from its (already re-homed) tail so deleteBlock only
        // removes this block itself and its own input/shadow subtrees.
        block.next = null;
        blocks.deleteBlock(blockId);
    };

    runtime.targets.forEach(target => {
        const blocks = target.blocks;
        if (!blocks || !blocks._blocks) return;
        const ids = [];
        for (const id of Object.keys(blocks._blocks)) {
            const block = blocks._blocks[id];
            if (block && typeof block.opcode === 'string' && block.opcode.startsWith(prefix)) {
                ids.push(id);
            }
        }
        ids.forEach(id => removeSingleBlock(blocks, id));
    });

    // Reload the current editing target so removed blocks disappear from the canvas.
    if (vm && typeof vm.refreshWorkspace === 'function') {
        vm.refreshWorkspace();
    }
};

// "Delete" a loaded extension so it can be re-added from scratch.
// scratch-vm has no first-class unload API, so we clear every registration
// trace the VM keeps: the manager's loaded sets, the compiled opcode-handler,
// the toolbox category (getBlocksXML()/toolbox is built from _blockInfo) and —
// crucial for URL/worker extensions — the workerURLs slot. isExtensionURLLoaded()
// checks workerURLs, so without freeing that slot the same URL could never be
// re-added (loadExtensionURL would early-return as "already loaded").
const removeExtension = (vm, extensionId) => {
    const em = vm && vm.extensionManager;
    const runtime = vm && vm.runtime;

    // First drop this extension's own blocks from every target (leaving the rest
    // of any containing script intact), so its code is really gone from scripts,
    // not just unloaded from the toolbox.
    removeExtensionBlocks(vm, extensionId);

    let serviceName = null;
    if (em && em._loadedExtensions) {
        serviceName = em._loadedExtensions.get(extensionId);
        em._loadedExtensions.delete(extensionId);
    }
    if (em && em._loadedExtensionInfos) {
        em._loadedExtensionInfos.delete(extensionId);
    }

    // Release the opcode-handler object bound onto the runtime for this extension.
    if (runtime && `ext_${extensionId}` in runtime) {
        delete runtime[`ext_${extensionId}`];
    }

    // Remove the toolbox category so it no longer appears in the blocks palette.
    if (runtime && Array.isArray(runtime._blockInfo)) {
        runtime._blockInfo = runtime._blockInfo.filter(category => category.id !== extensionId);
    }

    // Free worker resources / the URL-reuse guard. Service names look like
    // "extension.<workerId>.<extId>" or "unsandboxed.<workerId>.<extId>".
    if (em && serviceName) {
        const workerId = +String(serviceName).split('.')[1];
        if (Number.isFinite(workerId)) {
            if (em.workerURLs && em.workerURLs[workerId]) {
                delete em.workerURLs[workerId];
            }
            if (em.pendingWorkers && em.pendingWorkers[workerId]) {
                delete em.pendingWorkers[workerId];
            }
        }
    }

    // Rebuild the toolbox / emit workspace update so the removed category disappears.
    if (vm && typeof vm.emitWorkspaceUpdate === 'function') {
        vm.emitWorkspaceUpdate();
    }
};

const downloadExtension = async item => {
    if (!item.url) {
        return;
    }
    const res = await fetch(item.url);
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }
    const text = await res.text();
    const blob = new Blob([text], {type: 'text/javascript;charset=utf-8'});
    const objectURL = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectURL;
    anchor.download = `${item.id}.js`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectURL), 2000);
};

class LoadedExtensionsModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'refresh',
            'handleDelete',
            'handleDownload',
            'handlePreviewOpen',
            'handlePreviewClose',
            'handleOpenExtensionLibrary',
            'handleIconError'
        ]);
        this.state = {
            items: [],
            previewId: null
        };
    }
    componentDidMount () {
        // The modal is mounted/unmounted via `{loadedExtensionsModalVisible && ...}`,
        // so reaching mount means it is being shown — always (re)read the loaded
        // extensions. Do NOT gate this on this.props.visible: previously visible
        // was never passed in, so refresh() never ran and the list stayed empty.
        this.refresh();
    }
    componentDidUpdate (prevProps) {
        if (this.props.visible && !prevProps.visible) {
            this.refresh();
        }
    }
    refresh () {
        const items = collectLoadedExtensions(this.props.vm);
        this.setState({items});
        // Description/Version are not part of getInfo(), so they can only be
        // read back from the source — patch each row in as it arrives.
        items.forEach(item => {
            if (!item.url || (item.description && item.version)) {
                return;
            }
            fetchHeader(item.url).then(header => {
                if (!header.description && !header.version) {
                    return;
                }
                this.setState(prev => ({
                    items: prev.items.map(prevItem => (
                        prevItem.id === item.id ? {
                            ...prevItem,
                            description: prevItem.description || header.description || '',
                            version: prevItem.version || header.version || ''
                        } : prevItem
                    ))
                }));
            });
        });
    }
    handleDelete (item) {
        const used = countUsages(this.props.vm, item.id);
        const message = used > 0 ?
            `确定删除扩展「${item.name}」吗？\n\n当前工程中有 ${used} 个该扩展的积木，删除后它们会失效。` :
            `确定删除扩展「${item.name}」吗？`;
        // eslint-disable-next-line no-alert
        if (!window.confirm(message)) {
            return;
        }
        removeExtension(this.props.vm, item.id);
        this.refresh();
    }
    handleDownload (item) {
        downloadExtension(item).catch(err => {
            // eslint-disable-next-line no-alert
            window.alert(`下载扩展失败：${err.message || err}`);
        });
    }
    handlePreviewOpen (extensionId) {
        this.setState({previewId: extensionId});
    }
    handlePreviewClose () {
        this.setState({previewId: null});
    }
    handleOpenExtensionLibrary () {
        if (this.props.onCloseLoadedExtensions) {
            this.props.onCloseLoadedExtensions();
        }
        if (this.props.onOpenExtensionLibrary) {
            this.props.onOpenExtensionLibrary();
        }
    }
    handleIconError (event, item) {
        const target = event.target;
        if (!target || target.dataset.fallbackTried === '1') return;
        target.dataset.fallbackTried = '1';
        fetchIconAsDataURL(item.iconURL).then(dataURL => {
            if (dataURL) target.src = dataURL;
        });
    }
    render () {
        const {items, previewId} = this.state;
        return (
            <React.Fragment>
                <ModalComponent
                    fullScreen
                    className={styles.modalContent}
                    contentLabel="已加载的扩展"
                    onRequestClose={this.props.onCloseLoadedExtensions}
                >
                    <div className={styles.page}>
                        <div className={styles.listScroll}>
                            {items.length === 0 ? (
                                <div className={styles.empty}>当前没有已加载的扩展。</div>
                            ) : (
                                <div className={styles.list}>
                                    {items.map(item => (
                                        <div
                                            key={item.id}
                                            className={styles.card}
                                            style={{borderColor: item.color}}
                                        >
                                            {item.iconURL ? (
                                                <img
                                                    className={styles.icon}
                                                    src={item.iconURL}
                                                    draggable={false}
                                                    onError={e => this.handleIconError(e, item)}
                                                />
                                            ) : (
                                                <div
                                                    className={styles.iconTile}
                                                    style={{background: item.color}}
                                                    title={item.name}
                                                />
                                            )}
                                            <div className={styles.info}>
                                                <div
                                                    className={styles.name}
                                                    title={item.name}
                                                >
                                                    {item.name}
                                                </div>
                                                <div
                                                    className={styles.description}
                                                    title={item.description || ''}
                                                >
                                                    {item.description || '—'}
                                                </div>
                                            </div>
                                            <div className={styles.version}>
                                                {item.version ? `v${item.version}` : '—'}
                                            </div>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.button}
                                                    style={{background: item.color}}
                                                    onClick={() => this.handleDelete(item)}
                                                >
                                                    删除扩展
                                                </button>
                                                <button
                                                    className={styles.button}
                                                    style={{background: item.color}}
                                                    disabled={!item.url}
                                                    title={item.url ? '下载扩展源文件' : '内置扩展没有可下载的源文件'}
                                                    onClick={() => this.handleDownload(item)}
                                                >
                                                    下载扩展
                                                </button>
                                                <button
                                                    className={styles.button}
                                                    style={{background: item.color}}
                                                    onClick={() => this.handlePreviewOpen(item.id)}
                                                >
                                                    预览积木
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={styles.footer}>
                            <button
                                type="button"
                                className={styles.extensionButton}
                                title="进入扩展库"
                                onClick={() => this.handleOpenExtensionLibrary()}
                            >
                                <img
                                    className={styles.extensionButtonIcon}
                                    draggable={false}
                                    src={addExtensionIcon}
                                />
                            </button>
                        </div>
                    </div>
                </ModalComponent>
                {previewId !== null && (
                    <ModalComponent
                        fullScreen
                        contentLabel="扩展积木预览"
                        onRequestClose={this.handlePreviewClose}
                    >
                        <ExtensionPreviewComponent
                            vm={this.props.vm}
                            extensionId={previewId}
                            onClose={this.handlePreviewClose}
                        />
                    </ModalComponent>
                )}
            </React.Fragment>
        );
    }
}

LoadedExtensionsModal.propTypes = {
    vm: PropTypes.object,
    visible: PropTypes.bool,
    onCloseLoadedExtensions: PropTypes.func,
    onOpenExtensionLibrary: PropTypes.func
};

export default LoadedExtensionsModal;
